import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { apiBaseUrl } from '../config/api';
import { fallbackPages } from './fallbackContent';

function paragraphs(value) {
  return String(value || '').split(/\n\n/).filter(Boolean);
}

function Cta({ section, label, className = 'btn btn-primary', to }) {
  if (!label || section.button_state === 'hidden') return null;
  const text = section.button_state === 'sold_out' ? 'Ausgebucht' : label;
  const href = to || section.button_href;
  if (!href || section.button_state === 'sold_out') return <span className={className}>{text}</span>;
  return <Link to={href} className={className}>{text}</Link>;
}

function BulletList({ bullets, className }) {
  return (
    <ul className={className}>
      {(bullets || []).map((bullet) => (
        <li key={(bullet.lead || '') + bullet.text}>
          {bullet.lead ? <strong>{bullet.lead}:</strong> : null} {bullet.text}
        </li>
      ))}
    </ul>
  );
}

function Upcoming({ heading }) {
  const [events, setEvents] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const today = new Date();
    const from = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    fetch(`${apiBaseUrl}/api/events/?from=${from}`)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('events'))))
      .then((data) => {
        const list = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []);
        setEvents(list.slice(0, 3));
      })
      .catch(() => setFailed(true));
  }, []);
  if (failed) {
    return <section className="content-section"><div className="container"><h2>{heading || 'Als Nächstes bei Moveland'}</h2><p>Termine sind gerade nicht verfügbar.</p></div></section>;
  }
  if (!events || events.length === 0) return null;
  return (
    <section className="content-section">
      <div className="container">
        <h2>{heading || 'Als Nächstes bei Moveland'}</h2>
        {events.length === 0 && <p>Termine sind gerade nicht verfügbar.</p>}
        <div className="service-grid">
          {events.map((event) => {
            const full = event.capacity != null && event.spots_left <= 0;
            return (
              <article key={event.id} className="service-card" style={{ padding: '1.25rem' }}>
                <h3>{event.title}</h3>
                <p>{new Date(event.start_at).toLocaleString('de-CH', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}{event.location ? ` · ${event.location}` : ''}</p>
                <p>{event.capacity == null ? 'Freie Anmeldung' : full ? 'Ausgebucht' : `${event.registered_count} / ${event.capacity} Plätze belegt`}</p>
                {full ? <span className="btn btn-primary">Ausgebucht</span> : <Link to={`/events?event=${event.id}`} className="btn btn-primary">Anmelden</Link>}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function cardBySlot(content, slot) {
  return (content.cards || []).find((card) => card.slot === slot) || {};
}

const registry = {
  home: {
    hero: ({ section }) => <PageHero title={section.content.title || ''} subtitle={section.content.subtitle} variant="home" overlayOpacity={0.4} />,
    welcome: ({ section }) => (
      <section className="intro"><div className="container"><h2>{section.content.heading}</h2><p>{section.content.body}</p></div></section>
    ),
    upcoming_events: ({ section }) => <Upcoming heading={section.content.heading} />,
    expectations: ({ section }) => (
      <div className="container"><div className="services-header"><h2>{section.content.heading}</h2></div></div>
    ),
    line_dance: ({ section }) => (
      <div className="line-dance-focus">
        <div className="line-dance-focus-inner">
          <Link to="/services#line-dance" className="line-dance-image-link"><div className="line-dance-image"><img src="/images/line-dance.jpg" alt="Line Dance" /></div></Link>
          <div className="line-dance-content">
            <h3>{section.content.heading}</h3>
            <p className="line-dance-subtitle">{section.content.subtitle}</p>
            <p className="line-dance-band-lead">{section.content.body}</p>
            <BulletList bullets={section.content.bullets} className="line-dance-band-points" />
            <Cta section={section} label={section.content.button_label} className="line-dance-cta" to="/services#line-dance" />
          </div>
        </div>
      </div>
    ),
    offers: ({ section }) => {
      const western = cardBySlot(section.content, 'western');
      const farm = cardBySlot(section.content, 'farm');
      return (
        <div className="container">
          <div className="secondary-services">
            <div className="secondary-title-line"><h3>{section.content.heading}</h3></div>
            <div className="service-grid secondary-grid">
              <Link to="/services#westernreiten" className="service-card"><div className="service-image"><img src="/images/western-riding.jpeg" alt="Western Reiten" /></div><h3>{western.title}</h3><p>{western.body}</p></Link>
              <Link to="/services#landwirtschaft-hautnah" className="service-card"><div className="service-image"><img src="/images/bilder_rahel/land.jpeg" alt="Landwirtschaft" /></div><h3>{farm.title}</h3><p>{farm.body}</p></Link>
            </div>
          </div>
        </div>
      );
    },
    trial_course: ({ section }) => (
      <section className="content-section"><div className="container"><h2>{section.content.heading}</h2>{paragraphs(section.content.text).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}{section.linked_event && <p>{section.linked_event.title}</p>}<Cta section={section} label={section.content.button_label} /></div></section>
    ),
  },
  about: {
    hero: ({ section }) => <PageHero title={section.content.title || ''} subtitle={section.content.subtitle} className="about-hero" />,
    intro: ({ section }) => (
      <section className="content-section"><div className="container"><div className="about-intro"><div className="about-image"><img src="/images/rahel.jpeg" alt="Rahel Schmid" /></div><div className="about-text"><h2>{section.content.heading}</h2><p className="about-subtitle">{section.content.subtitle}</p><p>{section.content.body}</p></div></div></div></section>
    ),
    story: ({ section }) => (
      <section className="content-section"><div className="container"><div className="about-story-card"><h3>{section.content.heading}</h3>{paragraphs(section.content.body).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div></div></section>
    ),
    passions: ({ section }) => {
      const featured = cardBySlot(section.content, 'featured');
      const second = cardBySlot(section.content, 'secondary_1');
      const third = cardBySlot(section.content, 'secondary_2');
      return (
        <section className="content-section about-section-alt"><div className="container"><h2>{section.content.heading}</h2><p>{section.content.intro}</p><div className="passion-focus-card"><h3>{featured.title}</h3><p>{featured.body}</p></div><div className="passion-secondary-grid"><div className="passion-secondary-card"><h3>{second.title}</h3><p>{second.body}</p></div><div className="passion-secondary-card"><h3>{third.title}</h3><p>{third.body}</p></div></div></div></section>
      );
    },
    vision: ({ section }) => (
      <section className="content-section"><div className="container"><h2>{section.content.heading}</h2><p>{section.content.intro}</p><div className="vision-grid">{(section.content.cards || []).map((card) => <div className="vision-item" key={card.slot}><h3>{card.title}</h3><p>{card.body}</p></div>)}</div></div></section>
    ),
    contact_cta: ({ section }) => (
      <section className="content-section about-section-alt"><div className="container"><h2>{section.content.heading}</h2><p>{section.content.intro}</p><div className="cta-box"><h3>{section.content.box_heading}</h3><div className="contact-info"><a href={`mailto:${section.content.email}`} className="contact-link">{section.content.email}</a><span className="contact-separator">|</span><a href={`tel:${String(section.content.phone || '').replace(/\s/g, '')}`} className="contact-link">{section.content.phone}</a></div><Cta section={section} label={section.content.button_label} to="/contact" /></div></div></section>
    ),
  },
  services: {
    hero: ({ section }) => <PageHero title={section.content.title || ''} className="services-hero" />,
    line_dance_intro: ({ section }) => (
      <section id="line-dance" className="content-section service-spotlight-section"><div className="container"><div className="service-spotlight"><div className="service-spotlight-image"><img src="/images/line-dance.jpg" alt="Line Dance" /></div><div className="service-spotlight-content"><h2>{section.content.heading}</h2><p className="service-spotlight-subtitle">{section.content.subtitle}</p>{paragraphs(section.content.body).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}<BulletList bullets={section.content.bullets} className="service-spotlight-points" /></div></div></div></section>
    ),
    line_dance_facts: ({ section }) => (
      <section className="content-section"><div className="container"><div className="line-dance-priority-box"><h3>{section.content.heading}</h3><div className="service-quick-facts">{(section.content.facts || []).map((fact) => <div className="service-quick-fact" key={fact.slot}><span>{fact.label}</span><p>{fact.value}</p></div>)}</div><p className="line-dance-priority-note">{section.content.note}</p><Cta section={section} label={section.content.button_label} className="btn btn-primary service-spotlight-events-btn" to="/events" /></div></div></section>
    ),
    line_dance_levels: ({ section }) => (
      <section className="content-section"><div className="container"><div className="line-dance-levels">{(section.content.cards || []).map((card) => <div className="line-dance-level-card" key={card.slot}><h3>{card.title}</h3><p>{card.body}</p></div>)}</div></div></section>
    ),
    line_dance_room: ({ section }) => (
      <section className="content-section"><div className="container"><div className="line-dance-room-card"><div className="line-dance-room-image"><img src="/images/bilder_rahel/raum.jpeg" alt="Kursraum in Niederwil" /></div><div className="line-dance-room-text"><h3>{section.content.heading}</h3><p>{section.content.body}</p></div></div></div></section>
    ),
    western: ({ section }) => (
      <section id="westernreiten" className="content-section service-spotlight-section offer-spotlight-section"><div className="container"><div className="service-spotlight"><div className="service-spotlight-image"><img src="/images/western-riding.jpeg" alt="Westernreiten und Horsemanship" /></div><div className="service-spotlight-content"><h2>{section.content.heading}</h2><p className="service-spotlight-subtitle">{section.content.subtitle}</p>{paragraphs(section.content.body).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}<BulletList bullets={section.content.bullets} className="service-spotlight-points" /></div></div></div></section>
    ),
    farm: ({ section }) => (
      <section id="landwirtschaft-hautnah" className="content-section service-spotlight-section offer-spotlight-section"><div className="container"><div className="service-spotlight"><div className="service-spotlight-image"><img src="/images/bilder_rahel/land.jpeg" alt="Landwirtschaft hautnah" /></div><div className="service-spotlight-content"><h2>{section.content.heading}</h2><p className="service-spotlight-subtitle">{section.content.subtitle}</p>{paragraphs(section.content.body).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}<BulletList bullets={section.content.bullets} className="service-spotlight-points" /></div></div></div></section>
    ),
    cta: ({ section }) => (
      <section className="content-section services-cta-section"><div className="container"><div className="cta-box"><h3>{section.content.heading}</h3><p>{section.content.body}</p><div className="contact-info"><a href={`mailto:${section.content.email}`} className="contact-link">{section.content.email}</a><span className="contact-separator">|</span><a href={`tel:${String(section.content.phone || '').replace(/\s/g, '')}`} className="contact-link">{section.content.phone}</a></div><Cta section={section} label={section.content.button_label} to="/contact" /></div></div></section>
    ),
  },
  contact: {
    hero: ({ section }) => <PageHero title={section.content.title || ''} subtitle={section.content.subtitle} className="contact-hero" />,
    details: ({ section }) => (
      <div className="contact-info-section"><h2>{section.content.heading}</h2><p>{section.content.intro}</p><div className="contact-details"><div className="contact-detail-item"><h3>E-Mail</h3><a href={`mailto:${section.content.email}`} className="contact-link">{section.content.email}</a></div><div className="contact-detail-item"><h3>Telefon</h3><a href={`tel:${String(section.content.phone || '').replace(/\s/g, '')}`} className="contact-link">{section.content.phone}</a></div></div></div>
    ),
  },
};

export default function PageSections({ slug, sections, only }) {
  const source = (sections || fallbackPages[slug] || []).filter((section) => !only || only.includes(section.key));
  const list = slug === 'home' && !source.some((section) => section.key === 'hero')
    ? [fallbackPages.home.find((section) => section.key === 'hero'), ...source]
    : source;
  if (slug === 'home' && sections && !sections.some((section) => section.key === 'hero') && process.env.NODE_ENV !== 'production') {
    console.warn('Home response has no hero. Using the bundled hero.');
  }
  const homeBand = slug === 'home';
  const nodes = [];
  let band = [];
  const flush = () => {
    if (band.length) {
      nodes.push(<section className="services" key="home-services">{band}</section>);
      band = [];
    }
  };
  list.forEach((section) => {
    const Component = (registry[slug] || {})[section.key];
    if (!Component) {
      if (process.env.NODE_ENV !== 'production') console.warn('Unknown section', slug, section.key);
      return;
    }
    const node = <Component key={section.key} section={section} />;
    if (homeBand && ['expectations', 'line_dance', 'offers'].includes(section.key)) band.push(node);
    else {
      flush();
      nodes.push(node);
    }
  });
  flush();
  return <>{nodes}</>;
}
